// ─────────────────────────────────────────────
// KLOTHIQ — Task Form Modal (Create / Edit Task)
// ─────────────────────────────────────────────

import { Input, Select, Textarea } from '@/components/ui/FormControls';
import DatePicker from '@/components/common/DatePicker';
import { useState, useEffect } from 'react';
import { X, Calendar, Flag, User, ListChecks, Plus, Trash2 } from 'lucide-react';
import { useCreateTask, useUpdateTask } from '@/hooks/useTasks';
import { useOrgProfiles } from '@/hooks/useOrgProfiles';
import { workTaskDetailsSchema } from '@klothiq/validation';
import { useWorkIntent } from '@/hooks/useWorkIntent';
import WorkModalShell from '@/components/common/WorkModalShell';

interface TaskFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
  planTargetDate?: string | null;
  planArchived?: boolean;
  taskToEdit?: {
    id: string;
    version: number;
    checklist_error?: string;
    updated_at?: string;
    title: string;
    description: string | null;
    assignee_id: string | null;
    priority: string;
    due_date: string | null;
    checklist: Array<{ id: string; text: string; done: boolean }> | null;
  } | null;
}

export default function TaskFormModal({
  isOpen,
  onClose,
  planId,
  planTargetDate,
  planArchived,
  taskToEdit,
}: TaskFormModalProps) {
  const isEditing = !!taskToEdit;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal');
  const [dueDate, setDueDate] = useState('');
  const [checklist, setChecklist] = useState<Array<{ id: string; text: string; done: boolean }>>(
    [],
  );
  const [newChecklistText, setNewChecklistText] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [deadlineReason, setDeadlineReason] = useState('');
  const [baseVersion, setBaseVersion] = useState(1);
  const [baseline, setBaseline] = useState('');
  const intent = useWorkIntent();

  const {
    data: orgProfiles = [],
    isLoading: assigneesLoading,
    isError: assigneesError,
    refetch: reloadAssignees,
  } = useOrgProfiles(true);
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();

  useEffect(() => {
    intent.reset();
    setBaseVersion(taskToEdit?.version ?? 1);
    setDeadlineReason('');
    if (taskToEdit) {
      setTitle(taskToEdit.title || '');
      setDescription(taskToEdit.description || '');
      setAssigneeId(taskToEdit.assignee_id || '');
      setPriority((taskToEdit.priority as any) || 'normal');
      setDueDate(taskToEdit.due_date || '');
      setChecklist(taskToEdit.checklist || []);
    } else {
      setTitle('');
      setDescription('');
      setAssigneeId('');
      setPriority('normal');
      setDueDate('');
      setChecklist([]);
    }
    setNewChecklistText('');
    setErrorMsg(null);
    setBaseline(
      JSON.stringify([
        taskToEdit?.title ?? '',
        taskToEdit?.description ?? '',
        taskToEdit?.assignee_id ?? '',
        taskToEdit?.priority ?? 'normal',
        taskToEdit?.due_date ?? '',
        taskToEdit?.checklist ?? [],
      ]),
    );
  }, [taskToEdit?.id, isOpen]);

  if (!isOpen) return null;

  const handleAddChecklistItem = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = newChecklistText.trim();
    if (!trimmed) return;
    if (trimmed.length > 160 || checklist.length >= 30) {
      setErrorMsg('Use up to 30 checklist steps of 1–160 characters.');
      return;
    }
    setChecklist((prev) => [...prev, { id: crypto.randomUUID(), text: trimmed, done: false }]);
    setNewChecklistText('');
  };

  const handleRemoveChecklistItem = (id: string) => {
    setChecklist((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createTask.isPending || updateTask.isPending || planArchived) return;
    setErrorMsg(null);

    const trimmedTitle = title.trim();
    if (trimmedTitle.length < 3 || trimmedTitle.length > 160) {
      setErrorMsg('Task title must be between 3 and 160 characters.');
      return;
    }

    try {
      if (newChecklistText.trim())
        throw new Error('Add the checklist step or clear its input before saving.');
      if (taskToEdit?.checklist_error) throw new Error(taskToEdit.checklist_error);
      const fields = workTaskDetailsSchema.parse({
        title: trimmedTitle,
        description: description.trim() || null,
        priority,
        due_date: dueDate || null,
        checklist,
      });
      const needsReason =
        !!dueDate &&
        !!planTargetDate &&
        dueDate > planTargetDate &&
        (!isEditing || dueDate !== taskToEdit?.due_date);
      if (needsReason && (deadlineReason.trim().length < 3 || deadlineReason.trim().length > 500))
        throw new Error('Explain why this deadline is beyond the plan target (3–500 characters).');
      const exception = needsReason ? deadlineReason.trim() : undefined;
      if (isEditing && taskToEdit) {
        const input = {
          task_id: taskToEdit.id,
          expected_version: baseVersion,
          plan_id: planId,
          ...fields,
          deadline_exception_reason: exception,
        };
        await updateTask.mutateAsync({ ...input, request_key: intent.key('update_task', input) });
      } else {
        const input = {
          plan_id: planId,
          ...fields,
          assignee_id: assigneeId || null,
          deadline_exception_reason: exception,
        };
        await createTask.mutateAsync({ ...input, request_key: intent.key('create_task', input) });
      }
      intent.reset();
      onClose();
    } catch (err: any) {
      setErrorMsg(err.issues?.[0]?.message ?? err.message ?? 'Failed to save task.');
    }
  };

  const isPending = createTask.isPending || updateTask.isPending;
  const isDirty =
    !!newChecklistText.trim() ||
    !!deadlineReason.trim() ||
    baseline !== JSON.stringify([title, description, assigneeId, priority, dueDate, checklist]);
  const requestClose = () => {
    if (!isPending && (!isDirty || window.confirm('Discard your unsaved task changes?'))) onClose();
  };

  return (
    <WorkModalShell
      open={isOpen}
      onClose={requestClose}
      busy={isPending}
      title={isEditing ? 'Edit task details' : 'Add task'}
    >
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            {isEditing ? 'Edit Task Details' : 'Add New Task'}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Specify deliverables, deadline, and assign to a team member.
          </p>
        </div>
        <button
          onClick={requestClose}
          disabled={isPending}
          aria-label="Close task form"
          className="min-h-11 min-w-11 rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {errorMsg && (
        <div
          role="alert"
          className="mt-4 rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive"
        >
          {errorMsg}
        </div>
      )}

      {planArchived && (
        <p role="status" className="mt-3 text-sm text-muted-foreground">
          This plan is archived. Unarchive it before editing tasks.
        </p>
      )}
      <form onSubmit={handleSubmit} className="mt-4 flex min-h-0 flex-1 flex-col">
        <fieldset
          disabled={isPending || planArchived || !!taskToEdit?.checklist_error}
          className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto pr-1"
          aria-label="Task details"
        >
          {taskToEdit?.checklist_error && (
            <p role="alert" className="text-sm text-destructive">
              {taskToEdit.checklist_error}
            </p>
          )}
          <div>
            <label
              htmlFor="task-title"
              className="block text-xs font-semibold text-foreground mb-1"
            >
              Task Title <span className="text-destructive">*</span>
            </label>
            <Input
              id="task-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Photograph 30 launch items and export catalog"
              maxLength={160}
              required
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              helpLabel="Task Title *"
            />
          </div>

          <div>
            <label
              htmlFor="task-description"
              className="block text-xs font-semibold text-foreground mb-1"
            >
              Detailed Instructions / Scope
            </label>
            <Textarea
              id="task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide context, acceptance requirements, or step-by-step notes..."
              rows={3}
              maxLength={4000}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              helpLabel="Detailed Instructions / Scope"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Assignee Selection (Only active profiles with login accounts) */}
            <div>
              <label
                htmlFor="task-assignee"
                className="block text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5"
              >
                <User className="h-3.5 w-3.5 text-muted-foreground" /> Assignee
              </label>
              <Select
                id="task-assignee"
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                disabled={isEditing || assigneesLoading || assigneesError}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-base sm:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
                helpLabel="Assignee"
              >
                <option value="">Unassigned</option>
                {orgProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name} ({p.role || 'Staff'})
                  </option>
                ))}
              </Select>
              {assigneesLoading && (
                <p role="status" className="mt-1 text-xs text-muted-foreground">
                  Loading eligible team members…
                </p>
              )}
              {assigneesError && (
                <div role="alert" className="mt-2 text-sm text-destructive">
                  Team members could not be loaded.{' '}
                  <button
                    type="button"
                    className="min-h-11 underline"
                    onClick={() => reloadAssignees()}
                  >
                    Retry
                  </button>
                </div>
              )}
              {isEditing && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Use "Reassign" button in task view to change assignee with accountability note.
                </p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <label
                htmlFor="task-due-date"
                className="block text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5"
              >
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" /> Due Date
              </label>
              <DatePicker
                id="task-due-date"
                disabled={isPending}
                aria-label="Due date"
                value={dueDate}
                onChange={(nextDate) => setDueDate(nextDate ?? '')}
              />
            </div>
          </div>

          {!!dueDate &&
            !!planTargetDate &&
            dueDate > planTargetDate &&
            (!isEditing || dueDate !== taskToEdit?.due_date) && (
              <label className="block space-y-2 text-sm font-medium">
                Reason for deadline beyond {planTargetDate}
                <Input
                  value={deadlineReason}
                  onChange={(event) => setDeadlineReason(event.target.value)}
                  minLength={3}
                  maxLength={500}
                  required
                  className="min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-base"
                  helpLabel="Reason for deadline beyond"
                />
              </label>
            )}

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5">
              <Flag className="h-3.5 w-3.5 text-muted-foreground" /> Priority
            </label>
            <div className="grid grid-cols-4 gap-2">
              {(['low', 'normal', 'high', 'urgent'] as const).map((p) => {
                const isSelected = priority === p;
                return (
                  <button
                    key={p}
                    aria-pressed={isSelected}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`min-h-11 rounded-lg py-1.5 text-xs font-semibold capitalize border transition-all ${
                      isSelected
                        ? p === 'urgent'
                          ? 'bg-rose-500/20 border-rose-500 text-rose-500'
                          : p === 'high'
                            ? 'bg-amber-500/20 border-amber-500 text-amber-500'
                            : p === 'low'
                              ? 'bg-blue-500/20 border-blue-500 text-blue-500'
                              : 'bg-primary/20 border-primary text-primary'
                        : 'border-border bg-card text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Checklist items */}
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5">
              <ListChecks className="h-3.5 w-3.5 text-muted-foreground" /> Checklist Items (
              {checklist.length})
            </label>

            {checklist.length > 0 && (
              <div className="space-y-1.5 mb-2 max-h-36 overflow-y-auto pr-1">
                {checklist.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-2.5 py-1.5 text-xs"
                  >
                    <span className="truncate text-foreground">{item.text}</span>
                    <button
                      type="button"
                      aria-label={`Remove checklist step: ${item.text}`}
                      onClick={() => handleRemoveChecklistItem(item.id)}
                      className="min-h-11 min-w-11 inline-flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors shrink-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Input
                type="text"
                aria-label="New checklist step"
                value={newChecklistText}
                maxLength={160}
                onChange={(e) => setNewChecklistText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddChecklistItem();
                  }
                }}
                placeholder="Add sub-task checklist step..."
                className="min-h-11 min-w-0 flex-1 rounded-lg border border-input bg-background px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => handleAddChecklistItem()}
                disabled={checklist.length >= 30 || !newChecklistText.trim()}
                className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors flex items-center gap-1 shrink-0"
              >
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </div>
          </div>
        </fieldset>
        <div className="mt-4 flex shrink-0 items-center justify-end gap-2.5 pt-4 border-t border-border">
          <button
            type="button"
            onClick={requestClose}
            disabled={isPending}
            className="min-h-11 rounded-lg px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending || planArchived || !!taskToEdit?.checklist_error}
            className="min-h-11 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow hover:bg-primary/90 transition-all disabled:opacity-50"
          >
            {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Task'}
          </button>
        </div>
      </form>
    </WorkModalShell>
  );
}
